<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :width="'100%'"
    :mask-closable="false"
    :dialog-style="{ margin: '0', padding: '0', top: 'auto', bottom: '0', maxWidth: '100%' }"
    @cancel="handleCancel"
  >
    <!-- 标题栏 -->
    <template slot="title">
      <span class="dialog-title">分组管理</span>
    </template>

    <div class="group-manage">
      <!-- Tab 切换栏 -->
      <div class="tab-bar">
        <div
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab-item', activeTabId === tab.id ? 'active' : '']"
          @click="switchTab(tab.id)"
        >
          {{ tab.name }}({{ tab.students.length }})
        </div>
      </div>

      <!-- 学生列表区域 -->
      <div class="student-panel">
        <div class="student-grid">
          <div
            v-for="student in currentTabStudents"
            :key="student.id"
            :class="[
              'student-card',
              selectedIds[student.id] ? 'selected' : '',
              activeTabId !== UNGROUPED_ID ? 'in-group' : ''
            ]"
            @click="toggleSelect(student)"
          >
            <span class="student-seq">{{ student.serialNumber || '' }}</span>
            <span class="student-name">{{ student.realName || student.name || student.loginAccount }}</span>
          </div>
        </div>
      </div>

      <!-- 底部操作栏 -->
      <div class="bottom-bar">
        <!-- 待分组 Tab：移入分组 -->
        <template v-if="activeTabId === UNGROUPED_ID">
          <div class="bottom-left">
            <!-- 移入分组按钮 -->
            <div
              :class="['action-btn move-in-btn', hasSelected && customGroups.length > 0 ? 'active' : 'disabled']"
              @click="hasSelected && customGroups.length > 0 ? showMoveInPopover() : null"
            >
              <img class="btn-icon" :src="require('@/assets/common/add_group_icon.png')" alt="" />
              <span>移入分组</span>
            </div>
            <!-- 移入分组下拉浮层 -->
            <div v-if="moveInPopoverVisible" class="group-popover">
              <div
                v-for="group in customGroups"
                :key="group.id"
                :class="['popover-item', hoveredGroupId === group.id ? 'hovered' : '']"
                @mouseenter="hoveredGroupId = group.id"
                @mouseleave="hoveredGroupId = null"
                @click="moveSelectedToGroup(group.id)"
              >
                {{ group.name }}
              </div>
            </div>
            <span class="hint-text">
              <template v-if="hasSelected">
                请先选中学生，再点击此按钮 (当前已经选
                <span class="hint-count">{{ selectedCount }}</span>
                人)
              </template>
              <template v-else>请先选中学生，再点击此按钮</template>
            </span>
          </div>
        </template>

        <!-- 自定义分组 Tab：移出分组 + 删除分组 -->
        <template v-else>
          <div class="bottom-left">
            <!-- 移出分组按钮：直接将选中学生移回待分组 -->
            <div
              :class="['action-btn move-out-btn', hasSelected ? 'active' : 'disabled']"
              @click="hasSelected ? moveSelectedToGroup(UNGROUPED_ID) : null"
            >
              <img
                class="btn-icon"
                :src="
                  hasSelected
                    ? require('@/assets/common/remove_geoup_red_icon.png')
                    : require('@/assets/common/remove_geoup_icon.png')
                "
                alt=""
              />
              <span>移出分组</span>
            </div>
            <span class="hint-text">
              <template v-if="hasSelected">
                请先选中学生，再点击此按钮 (当前已经选
                <span class="remove-count">{{ selectedCount }}</span>
                人)
              </template>
              <template v-else>请先选中学生，再点击此按钮 (当前已经选 0人)</template>
            </span>
          </div>
          <!-- 删除分组 -->
          <div class="action-btn delete-btn" @click="confirmDeleteGroup">
            <img class="btn-icon" :src="require('@/assets/common/del_btn.png')" alt="" />
            <span>删除分组</span>
          </div>
        </template>
      </div>

      <!-- 底部输入栏 -->
      <div class="input-bar">
        <div class="group-name-input-wrap">
          <input
            v-model="newGroupName"
            class="group-name-input"
            placeholder="请输入分组名称（添加后不可修改分组名称）"
            @input="onGroupNameInput"
          />
          <!-- 清除按钮 -->
          <span v-if="newGroupName" class="input-clear" @click="newGroupName = ''">×</span>
          <div class="input-divider" />
          <!-- 添加按钮 -->
          <div :class="['add-btn', newGroupName.trim() ? 'active' : 'disabled']" @click="addGroup">添加</div>
        </div>
        <div class="footer-actions">
          <div class="footer-btn cancel-btn" @click="handleCancel">取消</div>
          <div :class="['footer-btn save-btn', isDirty ? 'active' : 'disabled']" @click="isDirty ? handleSave() : null">
            保存
          </div>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { cloneDeep } from 'lodash'
import { saveStudentGroups } from '@/api/common'
import { buttonStyleEnum } from '@/components/common/ConfirmModal/ButtonStyleEnum'

// 待分组 Tab 的固定 id
const UNGROUPED_ID = '__ungrouped__'

export default {
  name: 'GroupManageDialog',
  props: {
    // 班级 id，保存时需要
    clazzId: {
      type: [String, Number],
      default: ''
    },
    // 全量学生列表 [{ id, realName/name/loginAccount, seq/seqNum }]
    studentList: {
      type: Array,
      default: () => []
    },
    // 已有分组列表 [{ id, name, students: [{ id, ... }] }]
    groupList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      UNGROUPED_ID,
      visible: false,
      // 当前激活的 Tab id
      activeTabId: UNGROUPED_ID,
      // 内部维护的分组数据（深拷贝，不影响外部）
      // 格式：[{ id, name, studentIds: Set<id> }]
      innerGroups: [],
      // 选中的学生 id 映射 { [id]: true }
      selectedIds: {},
      // 移入分组浮层
      moveInPopoverVisible: false,
      // 浮层 hover 的分组 id
      hoveredGroupId: null,
      // 新增分组名称输入
      newGroupName: '',
      // 是否有未保存的操作
      isDirty: false,
      // 保存中
      saving: false
    }
  },
  computed: {
    // 所有 Tab（待分组 + 自定义分组）
    tabs() {
      const ungroupedStudents = this.ungroupedStudents
      return [
        { id: UNGROUPED_ID, name: '待分组学生', students: ungroupedStudents },
        ...this.innerGroups.map(g => ({
          id: g.id,
          name: g.name,
          students: this.studentList.filter(s => g.studentIds.has(s.id))
        }))
      ]
    },
    // 自定义分组列表（不含待分组）
    customGroups() {
      return this.innerGroups.map(g => ({
        id: g.id,
        name: g.name,
        students: this.studentList.filter(s => g.studentIds.has(s.id))
      }))
    },
    // 当前 Tab 的学生列表
    currentTabStudents() {
      const tab = this.tabs.find(t => t.id === this.activeTabId)
      return tab ? tab.students : []
    },
    // 待分组学生（不在任何分组中的）
    ungroupedStudents() {
      const groupedIds = new Set()
      this.innerGroups.forEach(g => g.studentIds.forEach(id => groupedIds.add(id)))
      return this.studentList.filter(s => !groupedIds.has(s.id))
    },
    // 当前 Tab 以外的其他分组（用于移出时选择目标）
    otherGroups() {
      return this.innerGroups.filter(g => g.id !== this.activeTabId).map(g => ({ id: g.id, name: g.name }))
    },
    // 是否有选中学生
    hasSelected() {
      return Object.values(this.selectedIds).some(Boolean)
    },
    // 选中学生数量
    selectedCount() {
      return Object.values(this.selectedIds).filter(Boolean).length
    }
  },
  methods: {
    // 打开弹窗，初始化数据
    // groupId: 可选，传入后默认选中对应分组的 Tab
    open(groupId) {
      // 深拷贝分组数据，转换 students 数组为 Set<id>
      this.innerGroups = cloneDeep(this.groupList).map(g => ({
        id: g.id,
        name: g.name,
        // 兼容 students 是对象数组或 id 数组
        studentIds: new Set((g.students || []).map(s => (typeof s === 'object' ? s.id : s)))
      }))
      // 若传入了 groupId 且该分组存在，则默认定位到该分组 Tab，否则默认待分组
      const targetGroup = groupId && this.innerGroups.find(g => g.id === groupId)
      this.activeTabId = targetGroup ? targetGroup.id : UNGROUPED_ID
      this.selectedIds = {}
      this.newGroupName = ''
      this.isDirty = false
      this.moveInPopoverVisible = false
      this.visible = true
    },

    // 切换 Tab
    switchTab(tabId) {
      if (tabId === this.activeTabId) return
      this.activeTabId = tabId
      // 切换 Tab 时清空选中
      this.selectedIds = {}
      this.moveInPopoverVisible = false
    },

    // 切换学生选中状态
    toggleSelect(student) {
      this.$set(this.selectedIds, student.id, !this.selectedIds[student.id])
    },

    // 获取当前选中的学生 id 列表（还原 id 类型，与 studentList 保持一致）
    getSelectedStudentIds() {
      return Object.keys(this.selectedIds)
        .filter(id => this.selectedIds[id])
        .map(id => {
          // Object.keys 返回字符串，若原始 id 是数字则还原为数字
          const num = Number(id)
          return Number.isFinite(num) && String(num) === id ? num : id
        })
    },

    // 显示移入分组浮层
    showMoveInPopover() {
      if (!this.customGroups.length) return
      this.moveInPopoverVisible = !this.moveInPopoverVisible
    },

    // 将选中学生移入目标分组（targetGroupId 为 UNGROUPED_ID 时移回待分组）
    moveSelectedToGroup(targetGroupId) {
      const ids = this.getSelectedStudentIds()
      if (!ids.length) return

      // 用新 Set 实例替换，确保 Vue 2 响应式更新
      const newGroups = this.innerGroups.map(g => {
        const newSet = new Set(g.studentIds)

        // 从当前分组移除选中学生
        if (g.id === this.activeTabId && this.activeTabId !== UNGROUPED_ID) {
          ids.forEach(id => newSet.delete(id))
        }

        // 加入目标分组
        if (g.id === targetGroupId && targetGroupId !== UNGROUPED_ID) {
          ids.forEach(id => newSet.add(id))
        }

        return { ...g, studentIds: newSet }
      })
      this.innerGroups = newGroups

      // 清空选中和浮层
      this.selectedIds = {}
      this.moveInPopoverVisible = false
      this.isDirty = true
    },

    // 添加新分组
    addGroup() {
      const name = this.newGroupName.trim()
      if (!name) return

      // 校验名称长度：不超过 10 个字符、20 个字节
      const byteLen = [...name].reduce((acc, ch) => acc + (ch.charCodeAt(0) > 127 ? 2 : 1), 0)
      if (name.length > 10 || byteLen > 20) {
        this.$message.warning('分组名称不能超过10个汉字哦')
        return
      }

      // 校验重名
      const isDuplicate = this.innerGroups.some(g => g.name === name)
      if (isDuplicate) {
        this.$message.warning('分组已存在')
        return
      }

      // 生成临时 id（保存后由后端返回真实 id）
      const tempId = `temp_${Date.now()}`
      this.innerGroups.push({ id: tempId, name, studentIds: new Set() })
      this.newGroupName = ''
      this.isDirty = true
      // 切换到新分组 Tab
      this.$nextTick(() => {
        this.switchTab(tempId)
      })
    },

    // 点击删除分组按钮
    async confirmDeleteGroup() {
      const group = this.innerGroups.find(g => g.id === this.activeTabId)
      if (!group) return

      // 分组内有学生时才弹确认框
      if (group.studentIds.size > 0) {
        const isConfirm = await this.$confirmModal({
          message: '确认删除分组？删除后，分组里的学生自动回到【待分组学生】中',
          confirmText: '确认',
          confirmBtnStyle: buttonStyleEnum.error
        })
        if (!isConfirm) return
      }

      this.doDeleteGroup()
    },

    // 执行删除分组
    async doDeleteGroup() {
      const groupId = this.activeTabId
      const group = this.innerGroups.find(g => g.id === groupId)
      if (!group) return

      // 临时分组（未保存过的）直接从本地移除
      if (String(groupId).startsWith('temp_')) {
        this.innerGroups = this.innerGroups.filter(g => g.id !== groupId)
        this.switchTab(UNGROUPED_ID)
        this.isDirty = true
        return
      }

      // 已保存的分组：从本地移除后调 handleSave 统一保存
      this.innerGroups = this.innerGroups.filter(g => g.id !== groupId)
      this.switchTab(UNGROUPED_ID)
      this.isDirty = true
    },

    // 保存
    async handleSave() {
      if (this.saving) return
      this.saving = true
      try {
        // 构造提交数据，接口字段为 groupName
        const groups = this.innerGroups.map(g => ({
          groupName: g.name,
          studentIds: Array.from(g.studentIds)
        }))
        await saveStudentGroups({ clazzId: this.clazzId, userGroupList: groups })
        this.isDirty = false
        // 通知父组件刷新分组数据
        this.$emit('saved')
        this.visible = false
      } catch (e) {
        this.$message.error('保存失败，请重试')
      } finally {
        this.saving = false
      }
    },

    // 取消/关闭
    handleCancel() {
      this.visible = false
      this.$emit('cancel')
    },

    // 输入分组名称时的处理（预留）
    onGroupNameInput() {}
  }
}
</script>

<style lang="scss" scoped>
.group-manage {
  display: flex;
  flex-direction: column;
  height: 700px;
}

/* Tab 切换栏 */
.tab-bar {
  display: flex;
  align-items: flex-end;
  padding: 0 0 0 68px;
  gap: 10px;
  flex-shrink: 0;

  .tab-item {
    height: 40px;
    padding: 0 20px;
    border-radius: 14px 14px 0 0;
    background: #f6f6f6;
    color: #999999;
    font-size: 17px;
    line-height: 40px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;

    &.active {
      background: linear-gradient(180deg, #66adff 0%, #1976ff 100%);
      color: #ffffff;
      font-weight: 600;
    }

    &:not(.active):hover {
      background: #ebebeb;
    }
  }
}

/* 学生列表区域 */
.student-panel {
  flex: 1;
  border: 1px solid #e5e5e5;
  border-radius: 24px 24px 0 0;
  overflow: hidden;
  position: relative;
  margin: 0 40px;

  .student-grid {
    height: 100%;
    overflow-y: auto;
    padding: 20px 28px;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 20px;
  }

  /* 浮动保存/取消按钮 */
  .float-actions {
    position: absolute;
    right: 28px;
    bottom: 20px;
    display: flex;
    gap: 14px;

    .float-btn {
      height: 40px;
      padding: 0 28px;
      border-radius: 30px;
      font-size: 17px;
      line-height: 40px;
      cursor: pointer;

      &.cancel-btn {
        border: 1px solid #c8c8c8;
        color: #999999;
        background: #f6f6f6;

        &:hover {
          background: #ebebeb;
        }
      }

      &.save-btn {
        background: linear-gradient(270deg, #66adff 0%, #1976ff 100%);
        color: #ffffff;

        &:hover {
          background: #0565f3;
        }
      }
    }
  }
}

/* 学生卡片 */
.student-card {
  width: 130px;
  height: 52px;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;
  overflow: hidden;
  flex-shrink: 0;

  &:hover {
    border-color: #bac8e0;
  }

  &.selected {
    border-color: #1976ff;
    background: rgba(232, 241, 255, 1);

    .student-name {
      color: #1976ff;
    }
  }

  /* 在自定义分组 Tab 中，选中状态改为红色 */
  &.in-group.selected {
    border-color: #ff3c30;
    background: rgba(255, 241, 240, 1);

    .student-name {
      color: #ff3c30;
    }
  }

  .student-seq {
    width: 30px;
    height: 24px;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 0 0 12px 0;
    font-size: 14px;
    color: #999999;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    align-self: flex-start;
  }

  .student-name {
    flex: 1;
    font-size: 20px;
    font-weight: 600;
    color: #333333;
    padding: 0 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* 底部操作栏 */
.bottom-bar {
  height: 60px;
  background: #f6f6f6;
  border-radius: 0 0 24px 24px;
  border-bottom: 1px solid #e5e5e5;
  border-left: 1px solid #e5e5e5;
  border-right: 1px solid #e5e5e5;
  margin: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
  flex-shrink: 0;
  position: relative;

  .bottom-left {
    display: flex;
    align-items: center;
    gap: 10px;
    position: relative;
  }

  .hint-text {
    font-size: 14px;
    color: #999999;
    line-height: 20px;

    .hint-count {
      color: #1976ff;
    }

    .remove-count {
      color: #ff3c30;
    }
  }
}

/* 操作按钮 */
.action-btn {
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    border-color 0.15s,
    color 0.15s;

  .btn-icon {
    width: 20px;
    height: 20px;
  }

  &.move-in-btn {
    color: #c8c8c8;

    &.active {
      border-color: #1dc060;
      color: #1dc060;
    }
  }

  &.move-out-btn {
    color: #c8c8c8;

    &.active {
      border-color: #ff3c30;
      color: #ff3c30;
    }
  }

  &.delete-btn {
    color: #ff3c30;
    border-color: #ff3c30;

    &:hover {
      background: rgba(255, 60, 48, 0.06);
    }
  }

  &.disabled {
    cursor: default;
  }
}

/* 分组选择浮层 */
.group-popover {
  position: absolute;
  bottom: 46px;
  left: 0;
  min-width: 186px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.1);
  z-index: 100;
  padding: 10px 0;

  .popover-item {
    height: 44px;
    padding: 0 14px;
    font-size: 17px;
    color: #333333;
    line-height: 44px;
    cursor: pointer;
    border-radius: 10px;
    margin: 0 14px;

    &.hovered {
      background: #f6f6f6;
    }
  }
}

/* 底部输入栏 */
.input-bar {
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 40px;
  gap: 14px;
  flex-shrink: 0;
  border-top: 1px solid #e5e5e5;
  margin-top: 20px;

  .group-name-input-wrap {
    flex: 0 0 500px;
    height: 40px;
    background: #f6f6f6;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    display: flex;
    align-items: center;
    padding: 0 6px 0 14px;

    .group-name-input {
      flex: 1;
      border: none;
      background: transparent;
      outline: none;
      font-size: 17px;
      color: #333333;

      &::placeholder {
        color: #c8c8c8;
      }
    }

    .input-clear {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.2);
      color: #ffffff;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      margin-right: 4px;
    }

    .input-divider {
      width: 1px;
      height: 20px;
      background: #e5e5e5;
      margin: 0 6px;
      flex-shrink: 0;
    }

    .add-btn {
      height: 28px;
      padding: 0 10px;
      border-radius: 6px;
      font-size: 14px;
      line-height: 28px;
      cursor: pointer;
      flex-shrink: 0;

      &.active {
        background: #1976ff;
        color: #ffffff;
      }

      &.disabled {
        background: rgba(186, 213, 255, 1);
        color: #ffffff;
        cursor: default;
      }
    }
  }

  .footer-actions {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-left: auto;

    .footer-btn {
      height: 40px;
      width: 180px;
      padding: 0 40px;
      border-radius: 30px;
      font-size: 17px;
      line-height: 40px;
      cursor: pointer;
      justify-content: center;
      display: flex;
      align-items: center;

      &.cancel-btn {
        border: 1px solid #c8c8c8;
        color: #999999;
        background: #ffffff;

        &:hover {
          background: #f6f6f6;
        }
      }

      &.save-btn {
        &.active {
          background: linear-gradient(270deg, #66adff 0%, #1976ff 100%);
          color: #ffffff;

          &:hover {
            background: #0565f3;
          }
        }

        &.disabled {
          background: rgba(186, 213, 255, 1);
          color: #ffffff;
          cursor: default;
        }
      }
    }
  }
}

/* 删除确认弹窗内容 */
.delete-confirm {
  padding: 8px 0 16px;

  .delete-confirm-text {
    font-size: 20px;
    color: #333333;
    line-height: 28px;
    margin-bottom: 20px;
  }

  .delete-confirm-btns {
    display: flex;
    gap: 20px;

    .confirm-btn {
      flex: 1;
      height: 54px;
      border-radius: 100px;
      font-size: 17px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      &.danger {
        background: #ea1d11;
        color: #ffffff;

        &:hover {
          background: #d01a0f;
        }
      }

      &.default {
        background: #f6f6f6;
        color: #666666;

        &:hover {
          background: #ebebeb;
        }
      }
    }
  }
}

/* 覆盖 Ant Design Modal 样式 */
::v-deep .ant-modal-wrap {
  display: flex;
  align-items: flex-end;
}

::v-deep .ant-modal {
  margin: 0 !important;
  padding-bottom: 0 !important;
  top: auto !important;
}

::v-deep .ant-modal-content {
  border-radius: 24px 24px 0 0;

  .ant-modal-header {
    height: 72px;
    display: flex;
    align-items: center;
    border-radius: 24px 24px 0 0;
    padding: 0 40px;
    border-bottom: none;
  }

  .ant-modal-close-x {
    width: 72px;
    height: 72px;
    line-height: 72px;
  }

  .ant-modal-body {
    padding: 0;
  }

  .anticon:hover {
    padding: 15px;
    background: #f6f6f6;
    border-radius: 50%;
  }
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  color: #333333;
}
</style>
